import App, { AppContext, AppProps } from 'next/app';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { IntlProvider } from 'react-intl';
import { LocalesAllowedValues } from 'models/Locales';
import { locales } from '@trustedshops/tps-seo-shared-frontend-react-components';
import 'fonts/helios/shop-search-font.css';
import { ModalTypes } from 'models/ModalTypes';
import { OnParameterChange } from 'models/OnParameterChange';
import { FilterableQueryParams } from 'models/FilterableQueryParams';
import {
  determineCountryCodeFromTld,
  determineEnvironmentFromHost,
  determineTldFromHost
} from 'helpers/url-mapper';
import { languageCodePerTld } from 'models/languageCodePerTld';
import { GTM_ID } from 'helpers/gtm';
import '@formatjs/intl-getcanonicallocales/polyfill';
import '@formatjs/intl-locale/polyfill';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/pl';
import Script from 'next/script';

/**
 * Loads translations from JSON files on server,
 * and from __NEXT_DATA__ cache on client.
 * As dynamic `import` is Promise-based, we have to use `getInitialProps`.
 * Using `require` would not solve the problem of having 1 file with all translations.
 */
async function getMessages(languageCode: string, locale: string) {
  if (typeof window === 'undefined') {
    const fileNotFound = () => {};
    const [
      languageSpecificTranslations,
      countrySpecificTranslations
    ]: LocalesAllowedValues[] = await Promise.all(
      [
        import(`locales/${languageCode}.json`),
        import(`locales/${locale}.json`)
      ].map((promise) => promise.then((file) => file.default, fileNotFound))
    );

    const [
      languageSpecificSharedTranslations,
      countrySpecificSharedTranslations
    ] = [languageCode, locale].map(
      (attribute) => locales[attribute as keyof typeof locales] || {}
    );

    return {
      ...languageSpecificSharedTranslations,
      ...languageSpecificTranslations,
      ...countrySpecificSharedTranslations,
      ...countrySpecificTranslations
    };
  }

  return window.__NEXT_DATA__.props.pageProps.messages as LocalesAllowedValues;
}

async function getInitialProps(appContext: AppContext) {
  const { pageProps } = await App.getInitialProps(appContext);

  const tld = determineTldFromHost(pageProps.host);
  const languageCode = languageCodePerTld[tld];
  const countryCode = determineCountryCodeFromTld(tld);
  const locale = `${languageCode}-${countryCode}`;

  return {
    pageProps: {
      ...pageProps,
      languageCode,
      locale,
      messages: await getMessages(languageCode, locale)
    }
  };
}

function CompanySearchApp({
  Component,
  pageProps
}: AppProps<
  Awaited<ReturnType<typeof getInitialProps>>['pageProps']
>): JSX.Element {
  const router = useRouter();
  const { languageCode, locale, messages, host } = pageProps;
  const [modalVisible, setModalVisible] = useState<ModalTypes>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const environment = determineEnvironmentFromHost(host);

  router.locale = languageCode;

  useEffect(() => {
    document.documentElement.lang = languageCode;
  });

  const onParameterChange: OnParameterChange = (newHref: {
    pathname: string;
    query: FilterableQueryParams;
  }) => {
    router.push(newHref);
  };

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={languageCode}
      messages={messages}
    >
      <Component
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onParameterChange={onParameterChange}
        {...pageProps}
      />
      {environment !== 'vrt' && (
        <Script
          id="gtm-script"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM_ID}');
              `
          }}
        />
      )}
    </IntlProvider>
  );
}

CompanySearchApp.getInitialProps = getInitialProps;

export default CompanySearchApp;
